import { lookupCustomer } from '@/app/actions/customer'
import type { IdentificationData } from '@/src/core/types/identification.types'
import {
	extractIdentificationData,
	extractLoyaltyId,
	extractLoyaltyProgram,
} from '@/src/core/utils/loyaltyUtils'
import { TerminalApiResponse } from '@adyen/api-library/lib/src/typings/terminal/models'
import { useCallback, useEffect, useRef, useState } from 'react'

interface UseLoyaltyCheckOptions {
	onCustomerIdentified?: (name: string, identificationData: IdentificationData) => void
	onCustomerNotFound?: () => void
	enabled?: boolean
}

interface UseLoyaltyCheckReturn {
	customerName: string
	identificationData: IdentificationData | undefined
	isLoading: boolean
	reset: () => void
}

/** Custom hook to handle loyalty program checking with proper cleanup and race condition prevention */
function useLoyaltyCheck(
	terminalResponse?: TerminalApiResponse,
	options: UseLoyaltyCheckOptions = {}
): UseLoyaltyCheckReturn {
	const { onCustomerIdentified, onCustomerNotFound, enabled = true } = options
	const [customerName, setCustomerName] = useState('')
	const [identificationData, setIdentificationData] = useState<IdentificationData | undefined>()
	const [isLoading, setIsLoading] = useState(false)
	const abortControllerReference = useRef<AbortController | undefined>(undefined)
	const isCheckingReference = useRef(false)

	const resetCustomer = useCallback(() => {
		setCustomerName('')
		setIdentificationData(undefined)
		onCustomerNotFound?.()
	}, [onCustomerNotFound])

	const setCustomer = useCallback(
		(name: string, idData: IdentificationData) => {
			setCustomerName(name)
			setIdentificationData(idData)
			onCustomerIdentified?.(name, idData)
		},
		[onCustomerIdentified]
	)

	const checkForLoyaltyPrograms = useCallback(
		async (signal: AbortSignal) => {
			try {
				if (!terminalResponse?.SaleToPOIResponse || !enabled) {
					return
				}

				// Check if we've been aborted
				if (signal.aborted) {
					return
				}

				setIsLoading(true)
				console.info('🔍 [LOYALTY_HOOK] Checking for loyalty programs in terminal response', {
					responsePresent: !!terminalResponse?.SaleToPOIResponse,
					timestamp: new Date().toISOString(),
				})

				const program = extractLoyaltyProgram(terminalResponse)
				if (!program) {
					console.info('ℹ️ [LOYALTY_HOOK] No loyalty program found in terminal response', {
						timestamp: new Date().toISOString(),
					})
					if (!signal.aborted) {
						resetCustomer()
					}
					return
				}

				const loyaltyId = extractLoyaltyId(program)
				const idData = extractIdentificationData(program)

				if (!loyaltyId || !idData) {
					console.info('ℹ️ [LOYALTY_HOOK] No loyalty ID or identification data found in program', {
						program,
						timestamp: new Date().toISOString(),
					})
					if (!signal.aborted) {
						resetCustomer()
					}
					return
				}

				// Set identification data immediately - this shows the panel even if customer lookup fails
				if (!signal.aborted) {
					setIdentificationData(idData)
					console.info('✅ [LOYALTY_HOOK] Identification data extracted', {
						loyaltyId,
						identificationData: idData,
						timestamp: new Date().toISOString(),
					})
				}

				// Check again before making the API call
				if (signal.aborted) {
					return
				}

				try {
					console.info('🔍 [LOYALTY_HOOK] Looking up customer with loyalty ID', {
						loyaltyId,
						timestamp: new Date().toISOString(),
					})
					const response = await lookupCustomer({ customerId: loyaltyId })

					// Final check before updating state
					if (signal.aborted) {
						return
					}

					const displayName = response.success ? response.data.displayName : undefined

					if (displayName) {
						console.info('✅ [LOYALTY_HOOK] Customer found successfully', {
							loyaltyId,
							displayName,
							timestamp: new Date().toISOString(),
						})
						setCustomer(displayName, idData)
					} else {
						console.info('ℹ️ [LOYALTY_HOOK] Customer not found or no display name', {
							loyaltyId,
							timestamp: new Date().toISOString(),
						})
						// Don't reset identification data - just clear customer name
						setCustomerName('')
					}
				} catch (error) {
					if (!signal.aborted) {
						console.error('❌ [LOYALTY_HOOK] Error looking up customer', {
							loyaltyId,
							error: error instanceof Error ? error.message : 'Unknown error',
							timestamp: new Date().toISOString(),
						})
						// Don't reset identification data - just clear customer name
						setCustomerName('')
					}
				}
			} catch (error) {
				if (!signal.aborted) {
					console.error('❌ [LOYALTY_HOOK] Error in checking for loyalty programs', {
						error: error instanceof Error ? error.message : 'Unknown error',
						timestamp: new Date().toISOString(),
					})
					resetCustomer()
				}
			} finally {
				if (!signal.aborted) {
					setIsLoading(false)
				}
				isCheckingReference.current = false
			}
		},
		[terminalResponse, enabled, resetCustomer, setCustomer]
	)

	useEffect(() => {
		// Abort any ongoing request
		if (abortControllerReference.current) {
			abortControllerReference.current.abort()
		}

		// Don't start a new check if one is already in progress or if disabled
		if (isCheckingReference.current || !enabled) {
			return
		}

		// Create new abort controller for this check
		const controller = new AbortController()
		abortControllerReference.current = controller
		isCheckingReference.current = true

		// Start the loyalty check
		void checkForLoyaltyPrograms(controller.signal)

		// Cleanup function
		return () => {
			if (abortControllerReference.current) {
				abortControllerReference.current.abort()
				abortControllerReference.current = undefined
			}
			isCheckingReference.current = false
			setIsLoading(false)
		}
	}, [checkForLoyaltyPrograms, enabled])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (abortControllerReference.current) {
				abortControllerReference.current.abort()
			}
		}
	}, [])

	return {
		customerName,
		identificationData,
		isLoading,
		reset: resetCustomer,
	}
}

export type { IdentificationData } from '@/src/core/types/identification.types'
export { useLoyaltyCheck }
export type { UseLoyaltyCheckOptions, UseLoyaltyCheckReturn }
